/**
 * Thin, page-level availability summary for the hub city picker.
 * Answers only "does this city have sessions, and on which days" so step 1 can
 * open the right next step without waiting for a network round trip.
 * It is never the source of truth for bookability — /events and the capacity
 * validate call stay authoritative.
 */
(function (global) {
  'use strict';
  if (global.WorkshopHubSummary) return;

  var HORIZON_DAYS = { store: 35, popup: 95 };
  var REFRESH_DEBOUNCE_MS = 400;
  var MIN_REFRESH_GAP_MS = 5000;

  var state = {
    wrap: null,
    hubType: '',
    byHandle: Object.create(null),
    byProductId: Object.create(null),
    loaded: false,
    promise: null,
    lastFetchAt: 0,
    refreshTimer: 0,
    pendingWhileHidden: false,
    streamUnsub: null,
    listeners: [],
  };

  function idle(fn, timeout) {
    if (global.requestIdleCallback) {
      global.requestIdleCallback(fn, { timeout: timeout || 2000 });
      return;
    }
    setTimeout(fn, 300);
  }

  function api() {
    return global.WorkshopCityEvents;
  }

  function summaryConfigured(root) {
    var API = api();
    if (!API || typeof API.fetchHubSummary !== 'function') return false;
    if (typeof API.resolveHubSummaryUrl !== 'function') return false;
    return !!API.resolveHubSummaryUrl(root || state.wrap);
  }

  function cardsIn(wrap) {
    if (!wrap || !wrap.querySelectorAll) return [];
    return Array.prototype.slice.call(wrap.querySelectorAll('[data-wbw-hub-city]'));
  }

  function collectProducts(wrap) {
    var seen = Object.create(null);
    var out = [];
    cardsIn(wrap).forEach(function (card) {
      var id = String(card.getAttribute('data-product-id') || '').trim();
      if (!id || seen[id]) return;
      seen[id] = true;
      out.push({
        id: id,
        handle: String(card.getAttribute('data-handle') || '').trim(),
        kind:
          String(card.getAttribute('data-venue-kind') || '').toLowerCase() === 'store'
            ? 'store'
            : 'popup',
      });
    });
    return out;
  }

  function indexCities(cities) {
    var byHandle = Object.create(null);
    var byProductId = Object.create(null);
    (cities || []).forEach(function (city) {
      if (!city) return;
      if (city.product_handle) byHandle[city.product_handle] = city;
      if (city.product_id) byProductId[String(city.product_id)] = city;
    });
    state.byHandle = byHandle;
    state.byProductId = byProductId;
    state.loaded = true;
  }

  function notify() {
    state.listeners.slice().forEach(function (fn) {
      try {
        fn(state);
      } catch (_) {}
    });
  }

  function load(opts) {
    opts = opts || {};
    if (!state.wrap || !summaryConfigured()) return Promise.resolve(null);
    if (state.promise && !opts.force) return state.promise;
    var products = collectProducts(state.wrap);
    if (!products.length) return Promise.resolve(null);
    state.lastFetchAt = Date.now();
    var p = api()
      .fetchHubSummary(state.wrap, {
        workshopType: state.hubType,
        products: products,
        horizonDays: HORIZON_DAYS,
      })
      .then(function (payload) {
        indexCities(payload && payload.cities);
        notify();
        return payload;
      })
      .catch(function () {
        // Endpoint missing or down: stay silent and let the hub use /events.
        state.promise = null;
        return null;
      });
    state.promise = p;
    return p;
  }

  function refresh(opts) {
    opts = opts || {};
    if (!state.wrap || !summaryConfigured()) return;
    if (global.document && global.document.hidden) {
      state.pendingWhileHidden = true;
      return;
    }
    if (state.refreshTimer) return;
    var since = Date.now() - state.lastFetchAt;
    var wait = since >= MIN_REFRESH_GAP_MS ? REFRESH_DEBOUNCE_MS : MIN_REFRESH_GAP_MS - since;
    state.refreshTimer = setTimeout(function () {
      state.refreshTimer = 0;
      state.pendingWhileHidden = false;
      load({ force: true });
    }, wait);
  }

  function bindStream() {
    if (state.streamUnsub) return;
    var Stream = global.WorkshopLiveStream;
    if (!Stream || typeof Stream.subscribe !== 'function') return;
    if (typeof Stream.isSupported === 'function' && !Stream.isSupported()) return;
    state.streamUnsub = Stream.subscribe(function (evt) {
      if (!evt) return;
      if (evt.type === 'catalog' || (evt.payload && evt.payload.scope === 'catalog')) {
        refresh();
      }
    });
    if (global.document) {
      global.document.addEventListener('visibilitychange', function () {
        if (global.document.hidden) return;
        if (state.pendingWhileHidden) refresh();
      });
    }
  }

  function init(wrap) {
    if (!wrap || state.wrap === wrap) return;
    state.wrap = wrap;
    state.hubType = String(wrap.getAttribute('data-wbw-hub-type') || '').trim();
    if (!summaryConfigured()) return;
    idle(function () {
      load().then(function () {
        bindStream();
      });
    });
  }

  function getCity(handleOrId) {
    var key = String(handleOrId || '').trim();
    if (!key) return null;
    return state.byHandle[key] || state.byProductId[key] || null;
  }

  function getCityFor(meta) {
    if (!meta) return null;
    return getCity(meta.handle) || getCity(meta.productId);
  }

  global.WorkshopHubSummary = {
    init: init,
    load: load,
    refresh: refresh,
    getCity: getCity,
    getCityFor: getCityFor,
    isReady: function () {
      return state.loaded;
    },
    isConfigured: function () {
      return summaryConfigured();
    },
    ready: function () {
      return state.promise || Promise.resolve(null);
    },
    onUpdate: function (fn) {
      if (typeof fn !== 'function') return function () {};
      state.listeners.push(fn);
      return function () {
        var i = state.listeners.indexOf(fn);
        if (i !== -1) state.listeners.splice(i, 1);
      };
    },
    HORIZON_DAYS: HORIZON_DAYS,
  };
})(typeof window !== 'undefined' ? window : this);
